/**
 * Learning Progress Service
 * 
 * Unified progress tracking across all learning modalities:
 * - Voice Agent (existing - continues using progressService.ts)
 * - Consonant Practice (new)
 * - Digital Reading (new)
 * 
 * Tables updated:
 * - voice_agent_sessions: Session records with mode_source column
 * - child_skill_progress: Aggregate skill-level stats
 * - child_vocabulary_mastery: Per-word/phoneme mastery tracking
 * 
 * @see progressService.ts for Voice Agent specific tracking (unchanged)
 */

import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';

// =============================================================================
// Types
// =============================================================================

export type LearningMode = 'voice_agent' | 'consonant_practice' | 'reading' | 'flashcards';

export interface LearningSession {
  childId: string;
  mode: LearningMode;
  skillId?: string;
  durationSeconds: number;
  metadata?: Record<string, unknown>;
}

export interface PhonemeExposure {
  phoneme: string;
  skillId?: string;
  timestamp: Date;
  wasSuccessful?: boolean;
  /** Parent feedback from mastery modal (consonant practice 2-round flow) */
  masteryFeedback?: 'learning' | 'mastered';
}

export interface WordExposure {
  wordId: string;
  word: string;
  skillId?: string;
}

export interface ConsonantPracticeSession extends LearningSession {
  mode: 'consonant_practice';
  phonemesScanned: PhonemeExposure[];
}

export interface ReadingSession extends LearningSession {
  mode: 'reading';
  bookId: string;
  bookTitle: string;
  pagesRead: number;
  totalPages: number;
  wordsExposed: WordExposure[];
}

export interface FlashcardLearningSession extends LearningSession {
  mode: 'flashcards';
  bookId: string;
  totalWords: number;
  correct: number;
  incorrect: number;
  accuracy: number;
}

// =============================================================================
// Session Recording
// =============================================================================

/**
 * Record a completed learning session from any modality
 * 
 * Creates a session record in voice_agent_sessions with mode_source
 * to distinguish between Voice Agent, Consonant Practice, and Reading.
 */
export async function recordLearningSession(session: LearningSession | FlashcardLearningSession): Promise<{
  success: boolean;
  sessionId?: string;
  error?: string;
}> {
  try {
    console.log('[LearningProgressService] Recording session:', {
      childId: session.childId,
      mode: session.mode,
      skillId: session.skillId,
      durationSeconds: session.durationSeconds,
    });

    const now = new Date();
    const startedAt = new Date(now.getTime() - session.durationSeconds * 1000);

    const modeForDb = session.mode === 'consonant_practice' ? 'consonant'
      : session.mode === 'flashcards' ? 'reading'
      : session.mode;

    const sessionRecord: Record<string, unknown> = {
      child_id: session.childId,
      mode: modeForDb,
      mode_source: session.mode,
      skill_id: session.skillId || null,
      state: 'completed',
      started_at: startedAt.toISOString(),
      ended_at: now.toISOString(),
      duration_seconds: session.durationSeconds,
    };

    if (session.mode === 'consonant_practice') {
      const cpSession = session as ConsonantPracticeSession;
      sessionRecord.word_productions = {
        phonemes_scanned: cpSession.phonemesScanned?.length || 0,
        successful_productions: cpSession.phonemesScanned?.filter(p => p.wasSuccessful).length || 0,
      };
      sessionRecord.child_turns = cpSession.phonemesScanned?.length || 0;
    } else if (session.mode === 'reading') {
      const readingSession = session as ReadingSession;
      sessionRecord.word_exposures = {
        book_id: readingSession.bookId,
        book_title: readingSession.bookTitle,
        pages_read: readingSession.pagesRead,
        total_pages: readingSession.totalPages,
        words_exposed: readingSession.wordsExposed?.length || 0,
        completion_percentage: readingSession.totalPages > 0
          ? Math.round((readingSession.pagesRead / readingSession.totalPages) * 100)
          : 0,
      };
    } else if (session.mode === 'flashcards') {
      const fcSession = session as FlashcardLearningSession;
      sessionRecord.word_exposures = {
        type: 'flashcards',
        book_id: fcSession.bookId,
        total_words: fcSession.totalWords,
        correct: fcSession.correct,
        incorrect: fcSession.incorrect,
        accuracy: fcSession.accuracy,
      };
    }

    // Insert session
    const { data: sessionData, error: sessionError } = await (supabase as any)
      .from('voice_agent_sessions')
      .insert(sessionRecord)
      .select('id')
      .single();

    if (sessionError) {
      console.error('[LearningProgressService] Session creation error:', sessionError);
      Sentry.captureException(sessionError instanceof Error ? sessionError : new Error(String(sessionError)), {
        tags: { flow: 'learning', step: 'record_session' },
      });
      return { success: false, error: sessionError.message };
    }

    const sessionId = sessionData?.id;

    // Update skill progress if skill was specified
    if (session.skillId) {
      await updateSkillProgressForSession(session);
    }

    console.log('[LearningProgressService] Session recorded:', sessionId);
    return { success: true, sessionId };

  } catch (error) {
    console.error('[LearningProgressService] Error recording session:', error);
    Sentry.captureException(error instanceof Error ? error : new Error(String(error)), {
      tags: { flow: 'learning', step: 'record_session' },
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Record a consonant practice session with phoneme exposures
 */
export async function recordConsonantPracticeSession(
  childId: string,
  durationSeconds: number,
  phonemesScanned: PhonemeExposure[]
): Promise<{ success: boolean; sessionId?: string; error?: string }> {
  // Determine primary skill from phonemes (most common skill_id)
  const skillCounts = new Map<string, number>();
  phonemesScanned.forEach(p => {
    if (p.skillId) {
      skillCounts.set(p.skillId, (skillCounts.get(p.skillId) || 0) + 1);
    }
  });
  
  let primarySkillId: string | undefined;
  let maxCount = 0;
  skillCounts.forEach((count, skillId) => {
    if (count > maxCount) {
      maxCount = count;
      primarySkillId = skillId;
    }
  });

  const session: ConsonantPracticeSession = {
    childId,
    mode: 'consonant_practice',
    skillId: primarySkillId,
    durationSeconds,
    phonemesScanned,
    metadata: {
      total_phonemes: phonemesScanned.length,
      successful_phonemes: phonemesScanned.filter(p => p.wasSuccessful).length,
    },
  };

  // Record the session
  const result = await recordLearningSession(session);

  // Record individual phoneme exposures for mastery tracking
  if (result.success) {
    await recordPhonemeExposures(childId, phonemesScanned, result.sessionId);
  }

  return result;
}

/**
 * Record a reading session with word exposures
 */
export async function recordReadingSession(
  childId: string,
  bookId: string,
  bookTitle: string,
  durationSeconds: number,
  pagesRead: number,
  totalPages: number,
  wordsExposed: WordExposure[],
  skillId?: string
): Promise<{ success: boolean; sessionId?: string; error?: string }> {
  const session: ReadingSession = {
    childId,
    mode: 'reading',
    skillId,
    durationSeconds,
    bookId,
    bookTitle,
    pagesRead,
    totalPages,
    wordsExposed,
    metadata: {
      book_id: bookId,
      pages_read: pagesRead,
      total_pages: totalPages,
      words_exposed: wordsExposed.length,
    },
  };

  // Record the session
  const result = await recordLearningSession(session);

  // Record individual word exposures for mastery tracking
  if (result.success && wordsExposed.length > 0) {
    await recordWordExposures(childId, wordsExposed, result.sessionId);
  }

  return result;
}

// =============================================================================
// Exposure Recording
// =============================================================================

/**
 * Record phoneme exposures from consonant practice
 * 
 * For consonant practice, we track phoneme productions rather than word mastery.
 * This updates child_skill_progress for the relevant skills.
 */
async function recordPhonemeExposures(
  childId: string,
  phonemes: PhonemeExposure[],
  sessionId?: string
): Promise<void> {
  if (phonemes.length === 0) return;

  console.log('[LearningProgressService] Recording phoneme exposures:', {
    childId,
    count: phonemes.length,
    successful: phonemes.filter(p => p.wasSuccessful).length,
  });

  // child_skill_progress writes skipped — skill_id tracking is being rebuilt.
  // TODO: restore child_skill_progress updates when skill_id tracking is re-enabled.
  void childId;
}

/**
 * Record word exposures from reading sessions
 * 
 * Updates child_vocabulary_mastery to track which words the child has seen.
 * Reading exposures don't count as "productions" but do count as exposures.
 */
async function recordWordExposures(
  childId: string,
  words: WordExposure[],
  sessionId?: string
): Promise<void> {
  if (words.length === 0) return;

  console.log('[LearningProgressService] Recording word exposures:', {
    childId,
    count: words.length,
  });

  // child_skill_progress writes skipped — skill_id tracking is being rebuilt.
  // Word-level vocabulary mastery is still recorded separately.
  // TODO: restore child_skill_progress exposure updates when skill_id tracking is re-enabled.
  void childId;
}

// =============================================================================
// Skill Progress Updates
// =============================================================================

/**
 * Update skill progress after a session
 */
async function updateSkillProgressForSession(session: LearningSession): Promise<void> {
  if (!session.skillId) return;

  const { data: existing } = await supabase
    .from('child_skill_progress')
    .select('id, total_sessions')
    .eq('child_id', session.childId)
    .eq('skill_id', session.skillId)
    .maybeSingle();

  if (existing) {
    await supabase
      .from('child_skill_progress')
      .update({
        total_sessions: (existing.total_sessions || 0) + 1,
        updated_at: new Date().toISOString(),
      })
      .eq('id', existing.id);
  } else {
    await supabase
      .from('child_skill_progress')
      .insert({
        child_id: session.childId,
        skill_id: session.skillId,
        status: 'learning',
        words_practiced: 0,
        words_mastered: 0,
        total_sessions: 1,
        total_exposures: 0,
        total_productions: 0,
        started_at: new Date().toISOString(),
      });
  }
}

// =============================================================================
// Phoneme to Skill Mapping
// =============================================================================

/**
 * Map a phoneme to its corresponding skill_id
 * 
 * Consonant cards use phonemes like /b/, /d/, /m/
 * These map to Phase 1 skills in skill_sequence
 */
export async function getSkillIdForPhoneme(phoneme: string): Promise<string | null> {
  // Normalize phoneme (remove slashes if present)
  const normalizedPhoneme = phoneme.replace(/\//g, '').toLowerCase();

  // Query skill_sequence for matching sound_pattern
  const { data } = await supabase
    .from('skill_sequence')
    .select('skill_id, sound_pattern')
    .or(`sound_pattern.ilike.%${normalizedPhoneme}%,skill_id.ilike.%${normalizedPhoneme}%`)
    .limit(1)
    .maybeSingle();

  return data?.skill_id || null;
}

/**
 * Batch lookup skill IDs for multiple phonemes
 */
export async function getSkillIdsForPhonemes(
  phonemes: string[]
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  
  // Get all Phase 1 skills (consonants are typically Phase 1)
  const { data: skills } = await supabase
    .from('skill_sequence')
    .select('skill_id, sound_pattern')
    .eq('phase', '1');

  if (!skills) return result;

  // Build lookup map
  for (const phoneme of phonemes) {
    const normalized = phoneme.replace(/\//g, '').toLowerCase();
    
    // Find matching skill
    const match = skills.find(s => 
      s.sound_pattern?.toLowerCase().includes(normalized) ||
      s.skill_id?.toLowerCase().includes(normalized)
    );
    
    if (match) {
      result.set(phoneme, match.skill_id);
    }
  }

  return result;
}

// =============================================================================
// Analytics Helpers
// =============================================================================

/**
 * Get session counts by mode for a child
 */
export async function getSessionCountsByMode(
  childId: string,
  since?: Date
): Promise<Record<LearningMode, { count: number; totalMinutes: number }>> {
  let query = supabase
    .from('voice_agent_sessions')
    .select('mode_source, duration_seconds')
    .eq('child_id', childId)
    .eq('state', 'completed');

  if (since) {
    query = query.gte('started_at', since.toISOString());
  }

  const { data: sessions } = await query;

  const result: Record<LearningMode, { count: number; totalMinutes: number }> = {
    voice_agent: { count: 0, totalMinutes: 0 },
    consonant_practice: { count: 0, totalMinutes: 0 },
    reading: { count: 0, totalMinutes: 0 },
    flashcards: { count: 0, totalMinutes: 0 },
  };

  if (!sessions) return result;

  for (const session of sessions) {
    const mode = (session.mode_source as LearningMode) || 'voice_agent';
    if (result[mode]) {
      result[mode].count++;
      result[mode].totalMinutes += Math.round((session.duration_seconds || 0) / 60);
    }
  }

  return result;
}

// =============================================================================
// Consonant Mastery (Phase 1 skill_sequence + child_skill_progress)
// =============================================================================

export interface ConsonantMasteryItem {
  phoneme: string;
  skillId: string;
  skillColor: string;
  sequenceOrder: number;
  status: 'mastered' | 'learning' | 'not_started';
  totalExposures: number;
  totalProductions: number;
  lastPracticed: string | null;
}

/**
 * Get consonant mastery data for a child by joining skill_sequence (phase=1)
 * with child_skill_progress. Returns one item per consonant phoneme.
 *
 * No new migration needed — derives from existing tables.
 */
export async function getConsonantMasteryForChild(
  childId: string
): Promise<ConsonantMasteryItem[]> {
  // Get all phase-1 consonant skills ordered by sequence
  const { data: skills, error: skillsError } = await supabase
    .from('skill_sequence')
    .select('skill_id, color, sequence_order')
    .eq('phase', '1')
    .order('sequence_order', { ascending: true });

  if (skillsError || !skills) {
    console.error('[LearningProgress] Failed to fetch consonant skills:', skillsError);
    Sentry.captureException(skillsError instanceof Error ? skillsError : new Error(String(skillsError)), {
      tags: { flow: 'learning', step: 'get_consonant_mastery' },
    });
    return [];
  }

  // Get child's progress for these skills
  const skillIds = skills.map(s => s.skill_id);
  const { data: progress, error: progressError } = await supabase
    .from('child_skill_progress')
    .select('skill_id, status, total_exposures, total_productions, updated_at')
    .eq('child_id', childId)
    .in('skill_id', skillIds);

  if (progressError) {
    console.error('[LearningProgress] Failed to fetch consonant progress:', progressError);
    Sentry.captureException(progressError instanceof Error ? progressError : new Error(String(progressError)), {
      tags: { flow: 'learning', step: 'get_consonant_mastery' },
    });
    return [];
  }

  // Build a map for quick lookup
  const progressMap = new Map(
    (progress || []).map(p => [p.skill_id, p])
  );

  // Extract phoneme from skill_id pattern: "1-b-red" → "b", "1-ck-red" → "ck"
  function extractPhoneme(skillId: string): string {
    const parts = skillId.split('-');
    // Pattern: "{phase}-{phoneme}-{color}" — phoneme is everything between first and last segment
    if (parts.length >= 3) {
      return parts.slice(1, -1).join('-');
    }
    return skillId;
  }

  return skills.map(skill => {
    const p = progressMap.get(skill.skill_id);
    const phoneme = extractPhoneme(skill.skill_id);

    let status: ConsonantMasteryItem['status'] = 'not_started';
    if (p) {
      status = p.status === 'mastered' ? 'mastered' : 'learning';
    }

    return {
      phoneme,
      skillId: skill.skill_id,
      skillColor: skill.color,
      sequenceOrder: skill.sequence_order,
      status,
      totalExposures: p?.total_exposures || 0,
      totalProductions: p?.total_productions || 0,
      lastPracticed: p?.updated_at || null,
    };
  });
}
